<template>
  <div @click="selectedItem = null">
    <breadcrumbs :home="(type === 'Tank' ? 'Tanks' : 'Bulk containers') + ': Reconcile inventory'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form">
        <v-container class="pt-4 px-6 pb-6" fluid>
          <v-row align="center" dense>
            <v-col cols="12" sm="4" md="3">
              <date-picker v-model="transaction.date" @change="refresh(vessels)" hide-details :disabled="readOnly"/>
            </v-col>
            <v-col cols="12" sm="4" v-if="sites.length>1">
              <v-autocomplete label="Site" dense outlined :items="sites" v-model="transaction.siteId" item-text="name" item-value="id" hide-details @change="refresh(vessels)" :disabled="readOnly"/>
            </v-col>
            <v-col cols="3" sm="2" v-if="type === 'Tank'">
              <v-switch label="Active only" dense hide-details class="ml-4 mt-0" v-model="transaction.activeOnly" :disabled="readOnly"/>
            </v-col>
          </v-row>
          <v-row dense class="mt-3">
            <v-col cols="6" sm="3">
              <v-select v-model="transaction.value" label="Adjust values" outlined dense hide-details :items="['None','Automatically','As specified']"
                        append-outer-icon="mdi-help-circle" :disabled="readOnly" @change="changeValue"
                        @click:append-outer="$store.dispatch('help', {title:'Inventory reconciliation',topicId:'RgADAAAA',articleId:'RwAlAAAA',ref:'reconcileValues'})"/>
            </v-col>
            <v-col cols="12" sm="4" v-if="company.connectedToAccounting && transaction.value !== 'None' && values">
              <account-picker v-model="transaction.expenseAccountRef" label="Inventory adjustment account"
                              :accounts="accounts" :disabled="readOnly"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pb-0">
              <outlined-block>
                <v-data-table width="100%" height="calc(100vh - 362px)"
                              calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer fixed-header
                              :items="vesselsList" item-key="id" no-data-text="None found"
                              class="no-sort"
                              :headers="[{text:'Name',value:'name',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                        {text:'Contents',value:'contents',width:'26%',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                        {text:'Book inventory',value:'book',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                        {text:'Physical inventory',value:'physical',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                        {text:'Adjust by',value:'diff',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'}]"
                              :item-class="item => item.id=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.id; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:header.physical>
                    Physical inventory
                    <v-tooltip bottom>
                      <template v-slot:activator="{on}">
                        <v-icon small v-on="on" @click="resetQuantities(false)">mdi-tag-off-outline</v-icon>
                      </template>
                      Set all physical quantities to zero
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{on}">
                        <v-icon small v-on="on" @click="resetQuantities(true)">mdi-refresh</v-icon>
                      </template>
                      Reset physical quantities to book quantities
                    </v-tooltip>
                  </template>

                  <template v-slot:item.contents="{item}">
                    <template v-if="item.contents.alcoholType">{{item.contents.alcoholName}}</template>
                    <div v-else class="warning--text">No contents. Use "Adjust inventory".</div>
                  </template>
                  <template v-slot:item.book="{item}">
                    <template v-if="item.contents.alcoholType">
                      <div :class="{'mt-n5':selectedItem === item.id}">
                        {{item.contents.volume | number(3)}}
                        {{item.contents.alcoholType==='Spirit'||!item.contents.volume?'LAA':'litres'}}
                      </div>
                      <div v-if="trackLitres && item.contents.alcoholType==='Spirit'" :class="{'mt-11':selectedItem === item.id}">{{item.contents.litres | number(3)}} litres</div>
                      <div v-if="trackKg" :class="{'mt-11':selectedItem === item.id}">{{item.contents.kg | number(3)}} kg</div>
                      <div v-if="trackStrength" :class="{'mt-11':selectedItem === item.id}">{{item.contents.strength | number(2)}}% alc./vol.</div>
                      <div v-if="transaction.value==='As specified'" :class="{'mt-11':selectedItem === item.id}">{{item.contents.value | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:item.physical="{item}">
                    <template v-if="item.contents.alcoholType">
                      <template v-if="selectedItem === item.id">
                        <alcohol-input :spirit="item.contents.alcoholType==='Spirit' || !item.contents.volume"
                                      :tare="item.tare" v-model="item.volume" :litres.sync="item.litres" :kg.sync="item.kg" :strength.sync="item.strength"
                                      @change="updateQuantity(item)"/>
                        <decimal-number-input v-if="transaction.value==='As specified'" v-model="item.value"/>
                      </template>
                      <template v-else>
                        <div :class="{'error--text':item.volume < 0}">
                          {{item.volume | number(3)}}
                          {{item.contents.alcoholType==='Spirit'||!item.contents.volume?'LAA':'litres'}}
                        </div>
                        <div v-if="trackLitres && item.contents.alcoholType==='Spirit'" :class="{'error--text':item.litres < 0}">{{item.litres | number(3)}} litres</div>
                        <div v-if="trackKg" :class="{'error--text':item.kg < 0}">{{item.kg | number(3)}} kg</div>
                        <div v-if="trackStrength" :class="{'error--text':item.strength < 0 || item.strength > 100}">{{item.strength | number(2)}}% alc./vol.</div>
                        <div v-if="transaction.value==='As specified'" :class="{'error--text':item.value < 0}">{{item.value | currency}}</div>
                      </template>
                    </template>
                  </template>
                  <template v-slot:item.diff="{item}">
                    <template v-if="item.contents.alcoholType">
                      <div>
                        <div :class="{'error white--text pa-1':item.volume - item.contents.volume, 'mt-n5':selectedItem === item.id}">
                          {{item.volume - item.contents.volume | number(3)}}
                          {{item.contents.alcoholType==='Spirit'||!item.contents.volume?'LAA':'litres'}}
                        </div>
                      </div>
                      <div :class="{'mt-11':selectedItem === item.id}" v-if="trackLitres && item.contents.alcoholType==='Spirit'">
                        <span :class="{'error white--text pa-1':item.litres - item.contents.litres}">
                          {{item.litres - item.contents.litres | number(3)}} litres
                        </span>
                      </div>
                      <div :class="{'mt-11':selectedItem === item.id}" v-if="trackKg">
                        <span :class="{'error white--text pa-1':item.kg - item.contents.kg}">
                          {{item.kg - item.contents.kg | number(3)}} kg
                        </span>
                      </div>
                      <div :class="{'mt-11':selectedItem === item.id}" v-if="trackStrength">
                        <span class="white--text pa-1">-</span>
                      </div>
                      <div v-if="transaction.value==='As specified'" :class="{'mt-11':selectedItem === item.id}">
                        <span :class="{'error white--text pa-1':item.value - item.contents.value}">
                          {{item.value - item.contents.value | currency}}
                        </span>
                      </div>
                    </template>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" hide-details/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-spacer/>
        <v-btn :plain="!readOnly" color="primary" @click="cancel">{{readOnly ? 'Done' : 'Cancel'}}</v-btn>
        <v-btn color="primary" outlined :loading="saving" @click="save(true)" v-if="!readOnly">Save as draft</v-btn>
        <v-btn color="primary" :loading="saving" @click="save(false)" v-if="!readOnly">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'

export default {
  name: 'ExciseVesselReconcileEdit',
  mixins: [confirmLeave],
	props: {
    id: {type: String, required:false},
    type: {type: String, required:true}
	},
	data() {
		return {
      valid: true,
      transaction: {exciseVessels:[], value:'None', type:this.type, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), draft:false, activeOnly:true,
                    expenseAccountRef:'',comments:'Inventory reconciliation',siteId:this.$store.getters.sites(true, this.inBond ? {B263:true,B265:true,B266:true} : undefined)[0].id},
      saving:false,
      accounts: [],
      vessels: [],
      selectedItem: null
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    vesselsList() {
      return this.type === 'Tank' ? this.vessels.filter(vessel => this.transaction.activeOnly ? vessel.active : true) : this.vessels;
    },
    sites() {
      return this.$store.getters.sites(true, {B263:true,B265:true,B266:true});
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write');
    },
    trackLitres() {
      return this.$store.getters.company.settings.trackLitres;
    },
    trackKg() {
      return this.$store.getters.company.settings.trackKg;
    },
    trackStrength() {
      return this.$store.getters.company.settings.trackStrength;
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    }
  },
	async created() {
    const results = await this.$apiCall(`query ExciseVesselReconcileEdit($id:ID!, $edit:Boolean!, $accounting:Boolean!) {
                                            exciseVesselReconciliation(id:$id) @include(if:$edit) {
                                              id,value,date,type,comments,expenseAccountRef,site{id},draft,activeOnly
                                              exciseVessels{id,volume,litres,kg,strength,value}
                                            }
                                            accounts @include(if:$accounting) {
                                              ref,parentRef,name,type,typeName
                                            }
                                            companyAccounts @skip(if:$edit) {inventoryAdjustment}
                                          }`,
                                        {id:this.id??'',edit:!!this.id,accounting:this.company.connectedToAccounting});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    if (this.company.connectedToAccounting)
      this.accounts = results.data.accounts;
    if (this.id) {
      this.transaction = results.data.exciseVesselReconciliation;
      this.transaction.siteId = this.transaction.site.id;
      delete this.transaction.site;
    }
    else
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
    await this.refresh(this.transaction.exciseVessels);
  },
	methods: {
    resetQuantities(toBook) {
      for (const vessel of this.vessels) {
        vessel.volume = toBook ? vessel.contents.volume : 0;
        vessel.litres = toBook || !this.trackLitres ? vessel.contents.litres : 0;
        vessel.kg = toBook || !this.trackKg  ? vessel.contents.kg : 0;
        vessel.strength = toBook || !this.trackStrength ? vessel.contents.strength : 0;
        vessel.value = toBook ? vessel.contents.value : 0;
      }
    },
    changeValue() {
      for (const vessel of this.vesselsList)
        this.updateQuantity(vessel);
    },
    updateQuantity(vessel) {
      if (this.transaction.value === 'As specified')
        vessel.value = vessel.contents.volume === 0 ? 0 : this.$roundNumber(vessel.contents.value * (vessel.volume / vessel.contents.volume));
    },
/*
    volumeClass(item) {
      if (this.trackLitres && item.contents.alcoholType==='Spirit')
        return this.transaction.value==='As specified' ? 'mb-3' : 'mt-2';
      return this.transaction.value==='As specified' ? 'mb-4' : 'mt-2';
    },
    */
    async refresh(existingVessels) {
      const vessels = []
      if (this.type === 'Tank')
        await this.$lazyListLoad(vessels, 'tanks2', `id,name,tare,active,contents {siteId,alcoholType,alcoholName,volume,litres,value,kg,strength}`, 100, [{field:'siteId', comparator:'eq', value:this.transaction.siteId}], [{name:'asof', value:this.transaction.date}]);
      else
        await this.$lazyListLoad(vessels, 'bulkContainers2', `id,name,tare,contents {siteId,alcoholType,alcoholName,volume,litres,value,kg,strength}`, 100, [{field:'siteId', comparator:'eq', value:this.transaction.siteId},{field:'contents.volume', comparator:'ne', value:'0'}], [{name:'asof', value:this.transaction.date}]);
      this.vessels = vessels.map(vessel => {
        // Use existing physical counts if they are there, otherwise use current book counts
        const setFrom = existingVessels.find(v => v.id === vessel.id) ?? vessel.contents;
        return Object.assign(vessel, {volume:setFrom.volume, litres:setFrom.litres, value:setFrom.value, kg:setFrom.kg, strength:setFrom.strength});
      });
    },
    cancel() {
      this.$router.back();
    },
		async save(draft) {
      if (!this.valid || this.vesselsList.some(vessel => vessel.contents.alcoholType && (vessel.litres < 0 || vessel.value < 0 || vessel.volume < 0 || vessel.kg < 0 || vessel.strength < 0 || vessel.strength > 100))) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.transaction.draft = draft;
      this.transaction.exciseVessels = this.vesselsList.filter(vessel => vessel.contents.alcoholType).map(vessel => (
        {id:vessel.id,volume:vessel.volume,litres:vessel.litres,kg:vessel.kg,strength:vessel.strength,
         value:this.value==='None'?vessel.contents.value:(this.value==='Automatically'?null:vessel.value)}
      ));

      this.saving = true;
      const results = await this.$apiCall(`mutation ExciseVesselReconcileEdit($input: ExciseVesselReconciliationInput!) {
                                              exciseVesselReconciliationCrupdate(input:$input) {id}
                                            }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`Created ${draft?'draft':''} inventory reconciliation`});
      }
		}
	}
}
</script>